import type {
  CampaignWebhookCreateRequestDto,
  CampaignWebhookResponseDto,
  CampaignWebhookUpdateRequestDto,
  WebhookHeaderDto,
} from '@shared/services/apiService/apiService';

export const campaignWebhookEvents = [
  'call.started',
  'call.ended',
  'analysis.completed',
  'conversation.completed',
] as const;
export type CampaignWebhookEvent = (typeof campaignWebhookEvents)[number];

export type CampaignWebhookHttpMethod = 'POST' | 'GET';
export type CampaignWebhookAuthType = 'none' | 'basic' | 'bearer';

export interface CampaignWebhookDraft {
  id?: string;
  name: string;
  events: CampaignWebhookEvent[] | null;
  lastEventFailure?: CampaignWebhookResponseDto['lastEventFailure'];
  isEnabled: boolean;
  url: string;
  httpMethod: CampaignWebhookHttpMethod;
  headers: WebhookHeaderDto[];
  bodyTemplate: string;
  authType: CampaignWebhookAuthType;
  authConfig: {
    username: string;
    password: string;
    token: string;
  };
  hasStoredAuth: boolean;
  originalAuthType: CampaignWebhookAuthType;
  isAuthDirty: boolean;
  originalValues?: {
    name: string;
    events: CampaignWebhookEvent[] | null;
    isEnabled: boolean;
    url: string;
    httpMethod: CampaignWebhookHttpMethod;
    headers: WebhookHeaderDto[];
    bodyTemplate: string;
    authType: CampaignWebhookAuthType;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignWebhookValidationErrors {
  name?: string;
  url?: string;
  headers?: string;
  bodyTemplate?: string;
  auth?: string;
  events?: string;
}

export interface CampaignWebhookValidationResult {
  isValid: boolean;
  errors: CampaignWebhookValidationErrors;
}

const DEFAULT_HTTP_METHOD: CampaignWebhookHttpMethod = 'POST';
const DEFAULT_AUTH_TYPE: CampaignWebhookAuthType = 'none';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeHttpMethod = (value: unknown): CampaignWebhookHttpMethod => {
  if (value === 'GET' || value === 'POST') {
    return value;
  }

  return DEFAULT_HTTP_METHOD;
};

const normalizeAuthType = (value: unknown): CampaignWebhookAuthType => {
  if (value === 'basic' || value === 'bearer' || value === 'none') {
    return value;
  }

  return DEFAULT_AUTH_TYPE;
};

const getNormalizedHeaders = (headers?: WebhookHeaderDto[] | null): WebhookHeaderDto[] => {
  if (!Array.isArray(headers)) {
    return [];
  }

  return headers.map((header) => ({
    key: header.key ?? '',
    value: header.value ?? '',
  }));
};

const stringifyBodyTemplate = (bodyTemplate?: object | null): string => {
  if (!isRecord(bodyTemplate)) {
    return '';
  }

  return JSON.stringify(bodyTemplate, null, 2);
};

const hasMaskedAuthConfig = (authConfig?: { masked: boolean } | null): boolean =>
  Boolean(authConfig && typeof authConfig === 'object' && authConfig.masked);

export const createEmptyCampaignWebhookDraft = (): CampaignWebhookDraft => ({
  name: '',
  events: null,
  isEnabled: true,
  url: '',
  httpMethod: DEFAULT_HTTP_METHOD,
  headers: [],
  bodyTemplate: '',
  authType: DEFAULT_AUTH_TYPE,
  authConfig: {
    username: '',
    password: '',
    token: '',
  },
  hasStoredAuth: false,
  originalAuthType: DEFAULT_AUTH_TYPE,
  isAuthDirty: false,
});

export const normalizeCampaignWebhook = (
  webhook: CampaignWebhookResponseDto,
): CampaignWebhookDraft => {
  const authType = normalizeAuthType(webhook.authType);
  const headers = getNormalizedHeaders(webhook.headers);
  const bodyTemplate = stringifyBodyTemplate(webhook.bodyTemplate);

  return {
    id: webhook.id,
    events: webhook.events ?? null,
    lastEventFailure: webhook.lastEventFailure,
    name: webhook.name ?? '',
    isEnabled: Boolean(webhook.isEnabled),
    url: webhook.url ?? '',
    httpMethod: normalizeHttpMethod(webhook.httpMethod),
    headers,
    bodyTemplate,
    authType,
    authConfig: {
      username: '',
      password: '',
      token: '',
    },
    hasStoredAuth: hasMaskedAuthConfig(webhook.authConfig),
    originalAuthType: authType,
    isAuthDirty: false,
    originalValues: {
      events: webhook.events ?? null,
      name: webhook.name ?? '',
      isEnabled: Boolean(webhook.isEnabled),
      url: webhook.url ?? '',
      httpMethod: normalizeHttpMethod(webhook.httpMethod),
      headers,
      bodyTemplate,
      authType,
    },
    createdAt: webhook.createdAt,
    updatedAt: webhook.updatedAt,
  };
};

export const sortCampaignWebhooks = (webhooks: CampaignWebhookDraft[]): CampaignWebhookDraft[] =>
  [...webhooks].sort((left, right) => {
    const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;

    return leftTime - rightTime;
  });

const isValidUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);

    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const shouldRequireCredentials = (draft: CampaignWebhookDraft): boolean => {
  if (draft.authType === 'none') {
    return false;
  }

  if (draft.id) {
    return draft.isAuthDirty || draft.originalAuthType !== draft.authType || !draft.hasStoredAuth;
  }

  return true;
};

const getTrimmedHeaders = (headers: WebhookHeaderDto[]): WebhookHeaderDto[] =>
  headers.map((header) => ({
    key: header.key.trim(),
    value: header.value.trim(),
  }));

export const validateCampaignWebhookDraft = (
  draft: CampaignWebhookDraft,
): CampaignWebhookValidationResult => {
  const errors: CampaignWebhookValidationErrors = {};
  const trimmedHeaders = getTrimmedHeaders(draft.headers);

  if (
    draft.events !== null &&
    (!Array.isArray(draft.events) ||
      draft.events.length < 1 ||
      draft.events.length > campaignWebhookEvents.length ||
      new Set(draft.events).size !== draft.events.length ||
      draft.events.some((event) => !campaignWebhookEvents.includes(event)))
  ) {
    errors.events =
      'Select one to four supported lifecycle events or retain legacy call-ended behavior.';
  }

  if (!draft.name.trim()) {
    errors.name = 'campaignPage.webhooks.validation.nameRequired';
  }

  if (!draft.url.trim()) {
    errors.url = 'campaignPage.webhooks.validation.urlRequired';
  } else if (!isValidUrl(draft.url.trim())) {
    errors.url = 'campaignPage.webhooks.validation.urlInvalid';
  }

  if (trimmedHeaders.some((header) => !header.key.length || !header.value.length)) {
    errors.headers = 'campaignPage.webhooks.validation.headersRequired';
  } else {
    const normalizedKeys = trimmedHeaders.map((header) => header.key.toLowerCase());
    if (new Set(normalizedKeys).size !== normalizedKeys.length) {
      errors.headers = 'campaignPage.webhooks.validation.headersDuplicate';
    }
  }

  if (draft.bodyTemplate.trim()) {
    try {
      const parsed = JSON.parse(draft.bodyTemplate);
      if (!isRecord(parsed)) {
        errors.bodyTemplate = 'campaignPage.webhooks.validation.bodyTemplateObject';
      }
    } catch {
      errors.bodyTemplate = 'campaignPage.webhooks.validation.bodyTemplateInvalid';
    }
  }

  if (shouldRequireCredentials(draft)) {
    if (draft.authType === 'basic') {
      if (!draft.authConfig.username.trim() || !draft.authConfig.password) {
        errors.auth = 'campaignPage.webhooks.validation.basicAuthRequired';
      }
    }

    if (draft.authType === 'bearer' && !draft.authConfig.token.trim()) {
      errors.auth = 'campaignPage.webhooks.validation.bearerAuthRequired';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

const parseBodyTemplate = (value: string): Record<string, unknown> | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  return JSON.parse(trimmedValue) as Record<string, unknown>;
};

const getAuthConfigPayload = (draft: CampaignWebhookDraft): Record<string, string> | undefined => {
  if (draft.authType === 'none') {
    if (!draft.id) {
      return undefined;
    }

    if (draft.originalAuthType !== 'none' || draft.isAuthDirty) {
      return {};
    }

    return undefined;
  }

  if (
    draft.id &&
    draft.originalAuthType === draft.authType &&
    draft.hasStoredAuth &&
    !draft.isAuthDirty
  ) {
    return undefined;
  }

  if (draft.authType === 'basic') {
    return {
      username: draft.authConfig.username.trim(),
      password: draft.authConfig.password,
    };
  }

  return {
    token: draft.authConfig.token.trim(),
  };
};

const getBaseWebhookPayload = (draft: CampaignWebhookDraft) => ({
  name: draft.name.trim(),
  events: draft.events,
  isEnabled: draft.isEnabled,
  url: draft.url.trim(),
  httpMethod: draft.httpMethod,
  headers: getTrimmedHeaders(draft.headers),
  authType: draft.authType,
});

export const buildCampaignWebhookCreateRequest = (
  draft: CampaignWebhookDraft,
): CampaignWebhookCreateRequestDto => {
  const authConfig = getAuthConfigPayload(draft);
  const bodyTemplate = parseBodyTemplate(draft.bodyTemplate);

  return {
    ...getBaseWebhookPayload(draft),
    ...(bodyTemplate ? { bodyTemplate } : {}),
    ...(authConfig ? { authConfig } : {}),
  };
};

const areHeadersEqual = (left: WebhookHeaderDto[], right: WebhookHeaderDto[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (header, index) => header.key === right[index]?.key && header.value === right[index]?.value,
  );
};

export const buildCampaignWebhookUpdateRequest = (
  draft: CampaignWebhookDraft,
): CampaignWebhookUpdateRequestDto => {
  const authConfig = getAuthConfigPayload(draft);
  const bodyTemplate = parseBodyTemplate(draft.bodyTemplate);
  const trimmedHeaders = getTrimmedHeaders(draft.headers);
  const originalValues = draft.originalValues;
  const payload: CampaignWebhookUpdateRequestDto = {};

  if (
    !originalValues ||
    JSON.stringify(draft.events?.slice().sort() ?? null) !==
      JSON.stringify(originalValues.events?.slice().sort() ?? null)
  ) {
    payload.events = draft.events;
  }

  if (!originalValues || draft.name.trim() !== originalValues.name) {
    payload.name = draft.name.trim();
  }

  if (!originalValues || draft.isEnabled !== originalValues.isEnabled) {
    payload.isEnabled = draft.isEnabled;
  }

  if (!originalValues || draft.url.trim() !== originalValues.url) {
    payload.url = draft.url.trim();
  }

  if (!originalValues || draft.httpMethod !== originalValues.httpMethod) {
    payload.httpMethod = draft.httpMethod;
  }

  if (!originalValues || !areHeadersEqual(trimmedHeaders, originalValues.headers)) {
    payload.headers = trimmedHeaders;
  }

  if (!originalValues || draft.bodyTemplate.trim() !== originalValues.bodyTemplate.trim()) {
    payload.bodyTemplate = (bodyTemplate ?? null) as unknown as object;
  }

  if (!originalValues || draft.authType !== originalValues.authType) {
    payload.authType = draft.authType;
  }

  if (authConfig !== undefined) {
    payload.authConfig = authConfig;
  }

  return payload;
};
